import { useAtomValue } from 'jotai';
import { kebabCase } from 'lodash';
import React, { useMemo } from 'react';
import type {
    PartialSupportResultsStatusFilters,
    RequestResultsStatus,
    SupportRequestResultsModalTableRow,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import {
    Button,
    CompareBarChip,
    DataGridProps,
    ExportIcon,
    FlexContainer,
    IconButton,
    SimpleTooltip,
} from '@placer-ui/components';
import { getFeatureFlags } from 'core/flow-control';
import { generateCsvFileName } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/utils';
import { downloadFile } from 'API/common/download-file-service/download-file-service';
import {
    useResolverJobResultsQuery,
    useUpdateResolverJobMutation,
} from 'features/offline-reports/support-requests/components/support-requests-modals/queries/resolver-bridge-queries';
import {
    formatStatusFromEnum,
    transformTableDataToCsv,
} from 'features/offline-reports/support-requests/components/support-requests-modals/utils/utils';
import { SaveAsTagButton } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/components/save-as-tag-button/save-as-tag-button';
import { statusMapStore } from 'features/offline-reports/support-requests/store/property-store';

const filtersOrder: RequestResultsStatus[] = [
    'Available',
    'MissingInfo',
    'ReviewRequired',
    'ProcessingFailed',
    'SensitiveLocation',
    'Edited',
];

interface TopFilterSectionProps {
    onFilterChange: (filter: RequestResultsStatus) => void;
    columns: DataGridProps<SupportRequestResultsModalTableRow>['columns'];
    filters: PartialSupportResultsStatusFilters;
    dataToDisplay: SupportRequestResultsModalTableRow[];
}

export const TopFiltersSection = ({
    onFilterChange,
    columns,
    filters,
    dataToDisplay,
}: TopFilterSectionProps) => {
    const { enable_poi_upload_save_as_tag } = getFeatureFlags();
    const { isFetching: isLoadingResolverJobResults } = useResolverJobResultsQuery();
    const { mutate: updateResolverJob, isLoading: isLoadingUpdateResolverJob } =
        useUpdateResolverJobMutation();
    const statusMap = useAtomValue(statusMapStore);

    const handleDownloadFile = async () => {
        let fileName = generateCsvFileName({ name: 'data_validation', withAccountName: false });
        const { fileData } = transformTableDataToCsv({
            columns,
            data: dataToDisplay,
            includeChallengeItems: false,
        });

        await downloadFile(fileData, fileName, true);
    };

    const handleRecheckCoverage = () => {
        updateResolverJob({
            itemIds: dataToDisplay
                .filter(({ status, isEdited }) => ['ProcessingFailed'].includes(status) || isEdited)
                .map(({ id }) => id),
        });
    };

    const showRecheckButton = !!statusMap?.['Edited'] || !!statusMap?.['ProcessingFailed'];

    const existingStatuses = useMemo(
        () =>
            Object.values(filtersOrder).filter(
                (status) => !!statusMap?.[status as RequestResultsStatus],
            ) as RequestResultsStatus[],
        [statusMap],
    );

    return (
        <FlexContainer justifyContent="space-between" alignItems="center" gap="16px">
            <FlexContainer gap="16px">
                {existingStatuses.map((status) => (
                    <CompareBarChip
                        isSelected={!!filters[status]}
                        isDisabled={!statusMap?.[status]}
                        key={status}
                        categoryText={`${formatStatusFromEnum({ status })}:`}
                        tagValue={`${statusMap?.[status] ?? 0}`}
                        testId={`${kebabCase(status)}-filter`}
                        onClick={() => onFilterChange(status)}
                    />
                ))}
            </FlexContainer>
            <FlexContainer gap="16px">
                {showRecheckButton && (
                    <Button
                        size="small"
                        testId="recheck-coverage"
                        variant="primary-gray"
                        onClick={handleRecheckCoverage}
                        disabled={isLoadingResolverJobResults || isLoadingUpdateResolverJob}
                        isLoading={isLoadingResolverJobResults || isLoadingUpdateResolverJob}
                    >
                        {isLoadingUpdateResolverJob ? 'Checking' : 'Recheck'} Coverage
                    </Button>
                )}
                {enable_poi_upload_save_as_tag && <SaveAsTagButton />}
                <SimpleTooltip title="Export CSV" appendToParent>
                    <IconButton
                        testId="export-csv"
                        size="small"
                        aria-label="Export CSV"
                        variant="secondary-gray"
                        Icon={<ExportIcon />}
                        onClick={handleDownloadFile}
                    />
                </SimpleTooltip>
            </FlexContainer>
        </FlexContainer>
    );
};
